import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Folder, FolderOpen, Plus, Search, Pin, Star, Share2 } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';
import Modal from '../components/Modal';
import ColorPicker from '../components/ColorPicker';
import SelectMenu from '../components/SelectMenu';
import ShareAccessModal from '../components/ShareAccessModal';
import { apiClient } from '../apiClient';
import VisibilityIndicator from '../components/VisibilityIndicator';
import { richTextToPlainText } from '../utils/richText';

const SORT_OPTIONS = {
  projects: [
    { value: 'updated_desc', label: 'Recently Updated', description: 'Newest activity first.' },
    { value: 'updated_asc', label: 'Least Recently Updated', description: 'Oldest activity first.' },
    { value: 'created_desc', label: 'Date Created (Newest)', description: 'Latest created first.' },
    { value: 'created_asc', label: 'Date Created (Oldest)', description: 'Earliest created first.' },
    { value: 'name_asc', label: 'Alphabetical (A-Z)', description: 'Sort by name ascending.' },
    { value: 'name_desc', label: 'Alphabetical (Z-A)', description: 'Sort by name descending.' },
    { value: 'color_asc', label: 'Color (A-Z)', description: 'Sort by accent color.' },
    { value: 'color_desc', label: 'Color (Z-A)', description: 'Sort by accent color, reverse.' }
  ],
  folders: [
    { value: 'updated_desc', label: 'Recently Updated', description: 'Newest activity first.' },
    { value: 'updated_asc', label: 'Least Recently Updated', description: 'Oldest activity first.' },
    { value: 'created_desc', label: 'Date Created (Newest)', description: 'Latest created first.' },
    { value: 'created_asc', label: 'Date Created (Oldest)', description: 'Earliest created first.' },
    { value: 'name_asc', label: 'Alphabetical (A-Z)', description: 'Sort by name ascending.' },
    { value: 'name_desc', label: 'Alphabetical (Z-A)', description: 'Sort by name descending.' },
    { value: 'color_asc', label: 'Color (A-Z)', description: 'Sort by accent color.' },
    { value: 'color_desc', label: 'Color (Z-A)', description: 'Sort by accent color, reverse.' }
  ],
  prompts: [
    { value: 'updated_desc', label: 'Recently Updated', description: 'Newest activity first.' },
    { value: 'updated_asc', label: 'Least Recently Updated', description: 'Oldest activity first.' },
    { value: 'created_desc', label: 'Date Created (Newest)', description: 'Latest created first.' },
    { value: 'created_asc', label: 'Date Created (Oldest)', description: 'Earliest created first.' },
    { value: 'name_asc', label: 'Alphabetical (A-Z)', description: 'Sort by name ascending.' },
    { value: 'name_desc', label: 'Alphabetical (Z-A)', description: 'Sort by name descending.' }
  ]
};

const getDateValue = (value) => {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sortByOption = (items, sortKey) => {
  const sorted = [...items];
  const byName = (a, b) => (a.name || '').localeCompare(b.name || '');
  const byColor = (a, b) => (a.color || '').localeCompare(b.color || '');
  const byCreated = (a, b) => getDateValue(a.created_at) - getDateValue(b.created_at);
  const byUpdated = (a, b) => getDateValue(a.updated_at) - getDateValue(b.updated_at);

  switch (sortKey) {
    case 'name_asc':
      return sorted.sort(byName);
    case 'name_desc':
      return sorted.sort((a, b) => byName(b, a));
    case 'created_asc':
      return sorted.sort(byCreated);
    case 'created_desc':
      return sorted.sort((a, b) => byCreated(b, a));
    case 'updated_asc':
      return sorted.sort(byUpdated);
    case 'updated_desc':
      return sorted.sort((a, b) => byUpdated(b, a));
    case 'color_asc':
      return sorted.sort(byColor);
    case 'color_desc':
      return sorted.sort((a, b) => byColor(b, a));
    default:
      return sorted.sort((a, b) => byUpdated(b, a));
  }
};

const splitPinned = (items, idKey) => {
  const pinnedItems = items
    .filter((item) => item.pinned_at)
    .sort((a, b) => getDateValue(a.pinned_at) - getDateValue(b.pinned_at));
  const pinnedIds = new Set(pinnedItems.map((item) => item[idKey]));
  const unpinned = items.filter((item) => !pinnedIds.has(item[idKey]));
  return { pinnedItems, unpinned };
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString();
  } catch (error) {
    return '—';
  }
};

const withAccent = (color) => ({
  borderColor: color ? `${color}66` : 'rgba(255,255,255,0.12)',
  boxShadow: color ? `0 0 0 1px ${color}66, 0 12px 30px rgba(0,0,0,0.45)` : undefined
});

function ActionMenu({ pinned, onEdit, onTogglePin, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="action-menu" ref={ref}>
      <button
        type="button"
        className="icon-btn menu-button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        aria-label="Open menu"
      >
        ⋮
      </button>
      {open && (
        <div className="action-menu-list">
          <button type="button" onClick={onEdit}>Edit</button>
          <button type="button" onClick={onTogglePin}>{pinned ? 'Unpin' : 'Pin'}</button>
          <button type="button" className="danger" onClick={onDelete}>Delete</button>
        </div>
      )}
    </div>
  );
}

export function ProjectCard({ project, onOpen, onEdit, onTogglePin, onDelete, pinned, showActions = true }) {
  return (
    <div
      className="tile-card"
      role="button"
      tabIndex={0}
      style={withAccent(project.color)}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="tile-card-header">
        <div className="tile-icon" style={{ color: project.color || '#3B82F6' }}>
          <FolderOpen className="w-6 h-6" />
        </div>
        <div className="tile-card-actions">
          <VisibilityIndicator visibility={project.visibility} />
          {showActions && pinned && (
            <div className="pin-indicator" data-tooltip="Pinned" aria-label="Pinned">
              <Pin className="w-3 h-3" />
            </div>
          )}
          {showActions && (
            <ActionMenu
              pinned={pinned}
              onEdit={(e) => { e?.stopPropagation?.(); onEdit(); }}
              onTogglePin={(e) => { e?.stopPropagation?.(); onTogglePin(); }}
              onDelete={(e) => { e?.stopPropagation?.(); onDelete(); }}
            />
          )}
        </div>
      </div>
      <h3 className="tile-title">{project.name}</h3>
      <p className="tile-sub">{project.description || 'No description yet.'}</p>
      <div className="tile-meta">Updated {formatDate(project.updated_at)}</div>
    </div>
  );
}

export function FolderCard({ folder, onOpen, onEdit, onTogglePin, onDelete, pinned, showActions = true }) {
  return (
    <div
      className="tile-card"
      role="button"
      tabIndex={0}
      style={withAccent(folder.color)}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="tile-card-header">
        <div className="tile-icon" style={{ color: folder.color || '#3B82F6' }}>
          <Folder className="w-6 h-6" />
        </div>
        <div className="tile-card-actions">
          <VisibilityIndicator visibility={folder.visibility} />
          {showActions && pinned && (
            <div className="pin-indicator" data-tooltip="Pinned" aria-label="Pinned">
              <Pin className="w-3 h-3" />
            </div>
          )}
          {showActions && (
            <ActionMenu
              pinned={pinned}
              onEdit={(e) => { e?.stopPropagation?.(); onEdit(); }}
              onTogglePin={(e) => { e?.stopPropagation?.(); onTogglePin(); }}
              onDelete={(e) => { e?.stopPropagation?.(); onDelete(); }}
            />
          )}
        </div>
      </div>
      <h3 className="tile-title">{folder.name}</h3>
      <p className="tile-sub">{folder.description || 'No description yet.'}</p>
      <div className="tile-meta">Updated {formatDate(folder.updated_at)}</div>
    </div>
  );
}

function EmptyState({ title, description, actionLabel, onAction }) {
  return (
    <div className="empty-state card-glass">
      <h3 className="text-xl font-bold mb-2">{title}</h3>
      <p className="text-gray-400 mb-4">{description}</p>
      {onAction && (
        <button className="btn-primary" type="button" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}

export function ProjectsIndexView() {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('updated_desc');
  const [createOpen, setCreateOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [deleteProject, setDeleteProject] = useState(null);
  const [deleteMode, setDeleteMode] = useState('trash_children');
  const [deleteTargetProjectId, setDeleteTargetProjectId] = useState('');
  const [formState, setFormState] = useState({
    name: '',
    description: '',
    category_id: '',
    color: '#3B82F6',
    visibility: 'private'
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const sortedCategories = useMemo(
    () => [...(categories || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [categories]
  );

  const loadProjects = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient.get('/api/projects');
      setProjects(data);
    } catch (err) {
      setError('Unable to load projects.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
    apiClient.get('/api/categories')
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]));
  }, []);


  const filteredProjects = useMemo(() => {
    const filtered = projects.filter((project) =>
      project.name.toLowerCase().includes(search.toLowerCase())
    );
    const { pinnedItems, unpinned } = splitPinned(filtered, 'project_id');
    return [...pinnedItems, ...sortByOption(unpinned, sort)];
  }, [projects, search, sort]);

  const openCreate = () => {
    setFormState({ name: '', description: '', category_id: '', color: '#3B82F6', visibility: 'private' });
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!formState.name.trim()) return;
    if (!formState.category_id) {
      setError('Select a category before creating a project.');
      return;
    }
    setSaving(true);
    const tempId = `temp-${Date.now()}`;
    const optimistic = {
      project_id: tempId,
      name: formState.name,
      description: formState.description,
      category_id: formState.category_id || null,
      color: formState.color,
      visibility: formState.visibility,
      updated_at: new Date().toISOString(),
      created_at: new Date().toISOString()
    };
    setProjects((prev) => [optimistic, ...prev]);
    setCreateOpen(false);
    try {
      const saved = await apiClient.post('/api/projects', {
        name: formState.name,
        description: formState.description,
        category_id: formState.category_id || null,
        color: formState.color,
        visibility: formState.visibility
      });
      setProjects((prev) => prev.map((item) => (item.project_id === tempId ? saved : item)));
    } catch (err) {
      setProjects((prev) => prev.filter((item) => item.project_id !== tempId));
      setError('Unable to create project.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editProject) return;
    if (!formState.name.trim()) return;
    if (!formState.category_id) {
      setError('Select a category before saving the project.');
      return;
    }
    setSaving(true);
    const previous = projects;
    const updated = projects.map((item) =>
      item.project_id === editProject.project_id
        ? { ...item, ...formState, updated_at: new Date().toISOString() }
        : item
    );
    setProjects(updated);
    setEditProject(null);
    try {
      await apiClient.patch(`/api/projects/${editProject.project_id}`, {
        name: formState.name,
        description: formState.description,
        category_id: formState.category_id || null,
        color: formState.color,
        visibility: formState.visibility
      });
    } catch (err) {
      setProjects(previous);
      setError('Unable to update project.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteProject) return;
    const previous = projects;
    setProjects((prev) => prev.filter((item) => item.project_id !== deleteProject.project_id));
    setDeleteProject(null);
    try {
      await apiClient.del(`/api/projects/${deleteProject.project_id}`, {
        mode: deleteMode,
        target_project_id: deleteMode === 'move_folders' ? deleteTargetProjectId : null
      });
    } catch (err) {
      setProjects(previous);
      setError('Unable to delete project.');
    }
  };

  const handleTogglePin = async (project) => {
    const nextPinnedAt = project.pinned_at ? null : new Date().toISOString();
    setProjects((prev) => prev.map((item) => (
      item.project_id === project.project_id
        ? { ...item, pinned_at: nextPinnedAt }
        : item
    )));
    try {
      await apiClient.patch(`/api/projects/${project.project_id}`, { pinned_at: nextPinnedAt });
    } catch (err) {
      setProjects((prev) => prev.map((item) => (
        item.project_id === project.project_id
          ? { ...item, pinned_at: project.pinned_at }
          : item
      )));
      setError('Unable to update pin.');
    }
  };

  if (error && error.toLowerCase().includes('access')) {
    return (
      <div className="min-h-screen p-6">
        <div className="card-glass max-w-3xl mx-auto text-center">
          <h1 className="text-2xl font-bold mb-2">Access required</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <button className="btn-primary" onClick={() => navigate('/projects')}>
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  if (error && error.toLowerCase().includes('access')) {
    return (
      <div className="min-h-screen p-6">
        <div className="card-glass max-w-3xl mx-auto text-center">
          <h1 className="text-2xl font-bold mb-2">Access required</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <button className="btn-primary" onClick={() => navigate('/projects')}>
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen projects-page">
      <div className="page-header">
        <div className="page-header-top">
          <Breadcrumbs items={[{ label: 'Projects' }]} />
          <button className="btn-primary" type="button" onClick={openCreate} disabled={saving}>
            <Plus className="w-4 h-4" /> New Project
          </button>
        </div>
        <div className="page-header-actions">
          <div className="search-field">
            <Search className="w-4 h-4" />
            <input
              type="text"
              placeholder="Search projects"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="sort-field">
            <div className="sort-label">Sort</div>
            <SelectMenu
              value={sort}
              options={SORT_OPTIONS.projects}
              placeholder="Sort projects"
              onChange={setSort}
            />
          </div>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="spinner"></div>
      ) : (
        <>
          {filteredProjects.length === 0 ? (
            <EmptyState
              title="No projects yet"
              description="Create your first project to organize prompts and workflows."
              actionLabel="Create Project"
              onAction={openCreate}
            />
          ) : (
            <div className="tile-grid">
              {filteredProjects.map((project) => (
                <ProjectCard
                  key={project.project_id}
                  project={project}
                  onOpen={() => navigate(`/projects/${project.project_id}`)}
                  pinned={!!project.pinned_at}
                  onEdit={() => {
                    setFormState({
                      name: project.name,
                      description: project.description || '',
                      category_id: project.category_id || '',
                      color: project.color || '#3B82F6',
                      visibility: project.visibility || 'private'
                    });
                    setEditProject(project);
                  }}
                  onTogglePin={() => handleTogglePin(project)}
                  onDelete={() => {
                    setDeleteProject(project);
                    setDeleteMode('trash_children');
                    setDeleteTargetProjectId('');
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}

      <Modal
        open={createOpen || !!editProject}
        title={editProject ? 'Edit Project' : 'Create Project'}
        onClose={() => {
          setCreateOpen(false);
          setEditProject(null);
        }}
        footer={
          <div className="modal-actions">
            <button className="btn-secondary" type="button" onClick={() => {
              setCreateOpen(false);
              setEditProject(null);
            }}>
              Cancel
            </button>
            <button className="btn-primary" type="button" onClick={editProject ? handleUpdate : handleCreate} disabled={saving}>
              {editProject ? 'Save' : 'Create'}
            </button>
          </div>
        }
      >
        <div className="modal-field">
          <label>Name</label>
          <input
            className="input-glass"
            value={formState.name}
            onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Project name"
          />
        </div>
        <div className="modal-field">
          <label>Description</label>
          <textarea
            className="input-glass"
            rows="3"
            value={formState.description}
            onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Optional description"
          />
        </div>
        <div className="modal-field">
          <label>Category</label>
          <SelectMenu
            value={formState.category_id}
            options={sortedCategories.map((category) => ({
              value: category.category_id,
              label: category.name
            }))}
            placeholder="Select a category"
            onChange={(value) => setFormState((prev) => ({ ...prev, category_id: value }))}
          />
        </div>
        <div className="modal-field">
          <label>Accent Color</label>
          <ColorPicker value={formState.color} onChange={(color) => setFormState((prev) => ({ ...prev, color }))} />
        </div>
        <div className="modal-field">
          <label>Visibility</label>
          <SelectMenu
            value={formState.visibility}
            options={[
              { value: 'private', label: 'Private', description: 'Only you can access this project.' },
              { value: 'public', label: 'Public', description: 'Anyone can view this project.' }
            ]}
            placeholder="Select visibility"
            onChange={(value) => setFormState((prev) => ({ ...prev, visibility: value }))}
          />
        </div>
      </Modal>

      <Modal
        open={!!deleteProject}
        title="Move Project To Trash?"
        onClose={() => setDeleteProject(null)}
        footer={
          <div className="modal-actions">
            <button className="btn-secondary" type="button" onClick={() => setDeleteProject(null)}>
              Cancel
            </button>
            <button
              className="btn-primary danger"
              type="button"
              onClick={handleDelete}
              disabled={deleteMode === 'move_folders' && !deleteTargetProjectId}
            >
              Move to Trash
            </button>
          </div>
        }
      >
        <div className="space-y-3 text-gray-300">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="delete-project-mode"
              checked={deleteMode === 'trash_children'}
              onChange={() => setDeleteMode('trash_children')}
            />
            Trash this project and all folders/prompts inside it.
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="delete-project-mode"
              checked={deleteMode === 'move_folders'}
              onChange={() => setDeleteMode('move_folders')}
            />
            Move folders and prompts to another project, then trash this project.
          </label>
          {deleteMode === 'move_folders' && (
            <div className="modal-field">
              <label>Move To Project</label>
              <SelectMenu
                value={deleteTargetProjectId}
                options={projects
                  .filter((item) => item.project_id !== deleteProject?.project_id)
                  .map((item) => ({ value: item.project_id, label: item.name }))}
                placeholder="Select destination project"
                onChange={setDeleteTargetProjectId}
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

export function ProjectFoldersView() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [folders, setFolders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('updated_desc');
  const [createOpen, setCreateOpen] = useState(false);
  const [editFolder, setEditFolder] = useState(null);
  const [deleteFolder, setDeleteFolder] = useState(null);
  const [formState, setFormState] = useState({
    name: '',
    description: '',
    category_id: '',
    color: '#3B82F6',
    visibility: 'private'
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const sortedCategories = useMemo(
    () => [...(categories || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [categories]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [projectRes, folderRes] = await Promise.all([
        apiClient.get(`/api/projects/${projectId}`),
        apiClient.get('/api/folders', { project_id: projectId })
      ]);
      const projectData = projectRes;
      const folderList = folderRes;
      setProject(projectData && projectData.project_id ? projectData : null);
      setFolders(folderList);
    } catch (err) {
      setError(err.message || 'Unable to load folders.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadData();
    apiClient.get('/api/categories')
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]));
  }, [loadData]);

  useEffect(() => {
    let mounted = true;
    const fetchUser = async () => {
      try {
        const data = await apiClient.get('/api/auth/me');
        if (mounted) setCurrentUser(data);
      } catch (err) {
        // ignore
      }
    };
    fetchUser();
    return () => {
      mounted = false;
    };
  }, []);

  const filteredFolders = useMemo(() => {
    const filtered = folders.filter((folder) =>
      folder.name.toLowerCase().includes(search.toLowerCase())
    );
    const { pinnedItems, unpinned } = splitPinned(filtered, 'folder_id');
    return [...pinnedItems, ...sortByOption(unpinned, sort)];
  }, [folders, search, sort]);

  const openCreate = () => {
    setFormState({ name: '', description: '', category_id: '', color: '#3B82F6', visibility: 'private' });
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!formState.name.trim()) return;
    if (!formState.category_id) {
      setError('Select a category before creating a folder.');
      return;
    }
    setSaving(true);
    const tempId = `temp-${Date.now()}`;
    const optimistic = {
      folder_id: tempId,
      name: formState.name,
      description: formState.description,
      category_id: formState.category_id || null,
      color: formState.color,
      visibility: formState.visibility,
      updated_at: new Date().toISOString(),
      created_at: new Date().toISOString()
    };
    setFolders((prev) => [optimistic, ...prev]);
    setCreateOpen(false);
    try {
      const saved = await apiClient.post('/api/folders', {
        name: formState.name,
        description: formState.description,
        category_id: formState.category_id || null,
        project_id: projectId,
        color: formState.color,
        visibility: formState.visibility
      });
      setFolders((prev) => prev.map((item) => (item.folder_id === tempId ? saved : item)));
    } catch (err) {
      setFolders((prev) => prev.filter((item) => item.folder_id !== tempId));
      setError('Unable to create folder.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editFolder) return;
    if (!formState.name.trim()) return;
    if (!formState.category_id) {
      setError('Select a category before saving the folder.');
      return;
    }
    setSaving(true);
    const previous = folders;
    const updated = folders.map((item) =>
      item.folder_id === editFolder.folder_id
        ? { ...item, ...formState, updated_at: new Date().toISOString() }
        : item
    );
    setFolders(updated);
    setEditFolder(null);
    try {
      await apiClient.patch(`/api/folders/${editFolder.folder_id}`, {
        name: formState.name,
        description: formState.description,
        category_id: formState.category_id || null,
        color: formState.color,
        visibility: formState.visibility
      });
    } catch (err) {
      setFolders(previous);
      setError('Unable to update folder.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteFolder) return;
    const previous = folders;
    setFolders((prev) => prev.filter((item) => item.folder_id !== deleteFolder.folder_id));
    setDeleteFolder(null);
    try {
      await apiClient.del(`/api/folders/${deleteFolder.folder_id}`);
    } catch (err) {
      setFolders(previous);
      setError('Unable to delete folder.');
    }
  };

  const handleTogglePin = async (folder) => {
    const nextPinnedAt = folder.pinned_at ? null : new Date().toISOString();
    setFolders((prev) => prev.map((item) => (
      item.folder_id === folder.folder_id
        ? { ...item, pinned_at: nextPinnedAt }
        : item
    )));
    try {
      await apiClient.patch(`/api/folders/${folder.folder_id}`, { pinned_at: nextPinnedAt });
    } catch (err) {
      setFolders((prev) => prev.map((item) => (
        item.folder_id === folder.folder_id
          ? { ...item, pinned_at: folder.pinned_at }
          : item
      )));
      setError('Unable to update pin.');
    }
  };

  const isOwner = !!(currentUser && project && currentUser.user_id === project.user_id);

  return (
    <div className="min-h-screen projects-page">
      <div className="page-header">
        <div className="page-header-top">
          <div className="flex items-center gap-3">
            <Breadcrumbs
              items={[
                { label: 'Projects', to: '/projects' },
                { label: project?.name || 'Project' }
              ]}
            />
            {project?.visibility && <VisibilityIndicator visibility={project.visibility} />}
          </div>
          <div className="flex items-center gap-3">
            {isOwner && (
              <button className="btn-secondary" type="button" onClick={() => setShowShare(true)}>
                <Share2 className="w-4 h-4" /> Share
              </button>
            )}
            {isOwner && (
              <button className="btn-primary" type="button" onClick={openCreate} disabled={saving}>
                <Plus className="w-4 h-4" /> New Folder
              </button>
            )}
          </div>
        </div>
        <div className="page-header-actions">
          <div className="search-field">
            <Search className="w-4 h-4" />
            <input
              type="text"
              placeholder="Search folders"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="sort-field">
            <div className="sort-label">Sort</div>
            <SelectMenu
              value={sort}
              options={SORT_OPTIONS.folders}
              placeholder="Sort folders"
              onChange={setSort}
            />
          </div>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="spinner"></div>
      ) : (
        <>
          {filteredFolders.length === 0 ? (
            <EmptyState
              title="No folders yet"
              description="Create folders to organize prompts inside this project."
              actionLabel="Create Folder"
              onAction={openCreate}
            />
          ) : (
            <div className="tile-grid">
              {filteredFolders.map((folder) => (
                <FolderCard
                  key={folder.folder_id}
                  folder={folder}
                  onOpen={() => navigate(`/projects/${projectId}/folders/${folder.folder_id}`)}
                  pinned={!!folder.pinned_at}
                  showActions={isOwner}
                  onEdit={() => {
                    setFormState({
                      name: folder.name,
                      description: folder.description || '',
                      category_id: folder.category_id || '',
                      color: folder.color || '#3B82F6',
                      visibility: folder.visibility || 'private'
                    });
                    setEditFolder(folder);
                  }}
                  onTogglePin={() => handleTogglePin(folder)}
                  onDelete={() => setDeleteFolder(folder)}
                />
              ))}
            </div>
          )}
        </>
      )}

      <Modal
        open={createOpen || !!editFolder}
        title={editFolder ? 'Edit Folder' : 'Create Folder'}
        onClose={() => {
          setCreateOpen(false);
          setEditFolder(null);
        }}
        footer={
          <div className="modal-actions">
            <button className="btn-secondary" type="button" onClick={() => {
              setCreateOpen(false);
              setEditFolder(null);
            }}>
              Cancel
            </button>
            <button className="btn-primary" type="button" onClick={editFolder ? handleUpdate : handleCreate} disabled={saving}>
              {editFolder ? 'Save' : 'Create'}
            </button>
          </div>
        }
      >
        <div className="modal-field">
          <label>Name</label>
          <input
            className="input-glass"
            value={formState.name}
            onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Folder name"
          />
        </div>
        <div className="modal-field">
          <label>Description</label>
          <textarea
            className="input-glass"
            rows="3"
            value={formState.description}
            onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Optional description"
          />
        </div>
        <div className="modal-field">
          <label>Category</label>
          <SelectMenu
            value={formState.category_id}
            options={sortedCategories.map((category) => ({
              value: category.category_id,
              label: category.name
            }))}
            placeholder="Select a category"
            onChange={(value) => setFormState((prev) => ({ ...prev, category_id: value }))}
          />
        </div>
        <div className="modal-field">
          <label>Accent Color</label>
          <ColorPicker value={formState.color} onChange={(color) => setFormState((prev) => ({ ...prev, color }))} />
        </div>
        <div className="modal-field">
          <label>Visibility</label>
          <SelectMenu
            value={formState.visibility}
            options={[
              { value: 'private', label: 'Private', description: 'Only you can access this folder.' },
              { value: 'public', label: 'Public', description: 'Anyone can view this folder.' }
            ]}
            placeholder="Select visibility"
            onChange={(value) => setFormState((prev) => ({ ...prev, visibility: value }))}
          />
        </div>
      </Modal>

      <Modal
        open={!!deleteFolder}
        title="Move Folder To Trash?"
        onClose={() => setDeleteFolder(null)}
        footer={
          <div className="modal-actions">
            <button className="btn-secondary" type="button" onClick={() => setDeleteFolder(null)}>
              Cancel
            </button>
            <button className="btn-primary danger" type="button" onClick={handleDelete}>
              Move to Trash
            </button>
          </div>
        }
      >
        <p className="text-gray-300">
          This folder and its prompts will be moved to trash for 30 days.
        </p>
      </Modal>

      <ShareAccessModal
        open={showShare}
        onClose={() => setShowShare(false)}
        resourceType="project"
        resourceId={projectId}
        resourceName={project?.name}
        isOwner={isOwner}
      />
    </div>
  );
}

export function FolderPromptsView() {
  const { projectId, folderId } = useParams();
  const [project, setProject] = useState(null);
  const [folder, setFolder] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('updated_desc');
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [projectRes, folderRes, promptRes] = await Promise.all([
        apiClient.get(`/api/projects/${projectId}`),
        apiClient.get(`/api/folders/${folderId}`),
        apiClient.get('/api/prompts', { folder_id: folderId })
      ]);
      const projectData = projectRes;
      const folderData = folderRes;
      const promptList = promptRes;
      setProject(projectData && projectData.project_id ? projectData : null);
      setFolder(folderData && folderData.folder_id ? folderData : null);
      setPrompts(promptList);
    } catch (err) {
      setError(err.message || 'Unable to load prompts.');
    } finally {
      setLoading(false);
    }
  }, [folderId, projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let mounted = true;
    const fetchUser = async () => {
      try {
        const data = await apiClient.get('/api/auth/me');
        if (mounted) setCurrentUser(data);
      } catch (err) {
        // ignore
      }
    };
    fetchUser();
    return () => {
      mounted = false;
    };
  }, []);

  const filteredPrompts = useMemo(() => {
    const filtered = prompts.filter((prompt) =>
      prompt.name.toLowerCase().includes(search.toLowerCase())
    );
    return sortByOption(filtered, sort);
  }, [prompts, search, sort]);

  const isOwner = !!(currentUser && folder && currentUser.user_id === folder.user_id);

  return (
    <div className="min-h-screen projects-page">
      <div className="page-header">
        <div className="page-header-top">
          <div className="flex items-center gap-3">
            <Breadcrumbs
              items={[
                { label: 'Projects', to: '/projects' },
                { label: project?.name || 'Project', to: `/projects/${projectId}` },
                { label: folder?.name || 'Folder' }
              ]}
            />
            {folder?.visibility && <VisibilityIndicator visibility={folder.visibility} />}
          </div>
          <div className="flex items-center gap-3">
            {isOwner && (
              <button className="btn-secondary" type="button" onClick={() => setShowShare(true)}>
                <Share2 className="w-4 h-4" /> Share
              </button>
            )}
            {isOwner && (
              <button
                className="btn-primary"
                type="button"
                onClick={() => navigate('/prompts/create', {
                  state: {
                    projectId,
                    folderId,
                    projectName: project?.name,
                    folderName: folder?.name
                  }
                })}
              >
                <Plus className="w-4 h-4" /> Create Prompt
              </button>
            )}
          </div>
        </div>
        <div className="page-header-actions">
          <div className="search-field">
            <Search className="w-4 h-4" />
            <input
              type="text"
              placeholder="Search prompts"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="sort-field">
            <div className="sort-label">Sort</div>
            <SelectMenu
              value={sort}
              options={SORT_OPTIONS.prompts}
              placeholder="Sort prompts"
              onChange={setSort}
            />
          </div>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="spinner"></div>
      ) : (
        <>
          {filteredPrompts.length === 0 ? (
            <EmptyState
              title="No prompts yet"
              description="Create a prompt to start using this folder."
              actionLabel={isOwner ? "Create Prompt" : undefined}
              onAction={isOwner ? () => navigate('/prompts/create', {
                state: {
                  projectId,
                  folderId,
                  projectName: project?.name,
                  folderName: folder?.name
                }
              }) : undefined}
            />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPrompts.map((prompt) => (
                <div
                  key={prompt.prompt_id}
                  className="card-glass cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/prompts/${prompt.prompt_id}`, {
                    state: {
                      backTo: `/projects/${projectId}/folders/${folderId}`,
                      breadcrumbs: [
                        { label: 'Projects', to: '/projects' },
                        { label: project?.name || 'Project', to: `/projects/${projectId}` },
                        { label: folder?.name || 'Folder', to: `/projects/${projectId}/folders/${folderId}` }
                      ]
                    }
                  })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/prompts/${prompt.prompt_id}`, {
                        state: {
                          backTo: `/projects/${projectId}/folders/${folderId}`,
                          breadcrumbs: [
                            { label: 'Projects', to: '/projects' },
                            { label: project?.name || 'Project', to: `/projects/${projectId}` },
                            { label: folder?.name || 'Folder', to: `/projects/${projectId}/folders/${folderId}` }
                          ]
                        }
                      });
                    }
                  }}
                >
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-bold text-lg">{prompt.name}</h3>
                    <div className="flex items-center gap-2">
                      <VisibilityIndicator visibility={prompt.visibility || 'private'} />
                      <span className="badge">{prompt.visibility || 'private'}</span>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {richTextToPlainText(prompt.description) || 'No description'}
                  </p>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4" />
                        {Number(prompt.average_rating ?? 0).toFixed(1)}
                      </span>
                      <span>{prompt.view_count ?? 0} views</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <ShareAccessModal
        open={showShare}
        onClose={() => setShowShare(false)}
        resourceType="folder"
        resourceId={folderId}
        resourceName={folder?.name}
        isOwner={isOwner}
      />
    </div>
  );
}
